import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { NAV, canManager } from "../lib/nav";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Menu, LogOut, ChevronLeft, User, Bell, History, Search, MapPin, Calendar, Crown, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import CraftedBy from "./CraftedBy";
import AssistantFAB from "./AssistantFAB";

const C = {
  canvas: "#FAF6EC",
  panel: "#FFFFFF",
  border: "#EFE4D0",
  borderSoft: "#F3EAD6",
  text: "#3B2722",
  muted: "#8B7355",
  softMuted: "#A58F7C",
  burgundy: "#8B1E1E",
  burgundySoft: "#FBEBEB",
  gold: "#C49A4A",
};

function greetingFor(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export const DashboardHeaderCtx = React.createContext(null);

export default function AppShell({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [locations, setLocations] = useState([]);
  const [selLoc, setSelLoc] = useState(user?.location_id || "all");
  const [now, setNow] = useState(new Date());
  const [notifCount, setNotifCount] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [activity, setActivity] = useState(() => {
    // Client-side activity log — last 30 acked/dismissed notifications, per user.
    // Persisted in localStorage so staff can re-open items they clicked past.
    try {
      const raw = window.localStorage.getItem("sm_notif_activity_v1");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [dashCtx, setDashCtx] = useState({ locationId: null, onLocationChange: null, notifCount: 0 });

  const pushActivity = React.useCallback((entry) => {
    setActivity((prev) => {
      const next = [entry, ...prev.filter((e) => e.id !== entry.id)].slice(0, 30);
      try { window.localStorage.setItem("sm_notif_activity_v1", JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);
  const clearActivity = () => {
    try { window.localStorage.removeItem("sm_notif_activity_v1"); } catch {}
    setActivity([]);
  };

  const isDashboard = location.pathname === "/";
  const activeItem = useMemo(() => NAV.find((n) => n.path === location.pathname), [location.pathname]);
  const visible = NAV.filter((n) => n.key === "manager" ? canManager(user) : (can(n.module || n.key, "view") || user?.is_admin));
  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  useEffect(() => { api.get("/locations").then(setLocations).catch(() => {}); }, []);
  useEffect(() => { const iv = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(iv); }, []);

  const loadNotifs = React.useCallback(() => Promise.all([
    api.get("/kds/notifications", { params: { unread: true } }).catch(() => []),
    api.get("/approvals/notifications", { params: { unread: true } }).catch(() => []),
  ]).then(([kds, appr]) => {
    // Merge both feeds into a single list the dropdown can render + action on.
    // KDS items carry `_kind: "kds"` so `openNotif` picks the right ack endpoint,
    // and `markAllNotifs` clears BOTH (previously KDS re-inflated the badge every 15s).
    const kdsList = (kds || [])
      .filter((n) => !n.acked)
      .map((n) => ({
        id: n.id,
        _kind: "kds",
        message: n.message || `Kitchen ticket ${n.ticket_id || ""}`.trim(),
        created_at: n.at || n.created_at,
      }));
    const apprList = (appr || []).map((n) => ({ ...n, _kind: "approval" }));
    const merged = [...apprList, ...kdsList].sort((a, b) =>
      (b.created_at || "").localeCompare(a.created_at || ""));
    setNotifs(merged);
    setNotifCount(merged.length);
  }), []);

  useEffect(() => {
    let cancelled = false;
    const run = () => { if (!cancelled) loadNotifs(); };
    run();
    const iv = setInterval(run, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [loadNotifs]);

  const openNotif = async (n) => {
    try {
      if (n._kind === "kds") await api.post(`/kds/notifications/${n.id}/ack`);
      else await api.post(`/approvals/notifications/${n.id}/read`);
    } catch { /* ignore, next poll will reconcile */ }
    setNotifs((x) => x.filter((y) => y.id !== n.id));
    setNotifCount((c) => Math.max(0, c - 1));
    const target = n._kind === "kds"
      ? "/kds"
      : (n.workflow_type === "OWNER_DIGEST" ? "/digest" : "/inbox");
    pushActivity({
      id: n.id, _kind: n._kind, message: n.message,
      created_at: n.created_at, acted_at: new Date().toISOString(),
      action: "opened", target,
    });
    loadNotifs();
    navigate(target);
  };
  const markAllNotifs = async () => {
    try {
      // Approvals — server-side batch. KDS has no batch endpoint, so ack each.
      await api.post("/approvals/notifications/mark-all-read").catch(() => {});
      const kdsUnread = notifs.filter((n) => n._kind === "kds");
      await Promise.all(kdsUnread.map((n) =>
        api.post(`/kds/notifications/${n.id}/ack`).catch(() => {})));
    } catch { /* ignore */ }
    const nowIso = new Date().toISOString();
    notifs.forEach((n) => pushActivity({
      id: n.id, _kind: n._kind, message: n.message,
      created_at: n.created_at, acted_at: nowIso,
      action: "dismissed",
      target: n._kind === "kds"
        ? "/kds"
        : (n.workflow_type === "OWNER_DIGEST" ? "/digest" : "/inbox"),
    }));
    setNotifs([]); setNotifCount(0);
    loadNotifs();
  };
  const reopenActivity = (a) => {
    navigate(a.target || "/inbox");
  };

  const doLogout = async () => { await logout(); navigate("/login"); };
  const scoped = !!user?.location_id && !user?.is_admin;
  const effectiveLoc = scoped ? user.location_id : (selLoc === "all" ? null : selLoc);

  const greeting = greetingFor(now.getHours());
  const dateStr = now.toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  const shortDate = now.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
  const firstName = (user?.name || "").split(" ")[0] || "there";

  const pageTitle = isDashboard ? `${greeting}, ${firstName}` : (activeItem?.label || "Sir Monty");
  const pageSubtitle = isDashboard ? "Here's what's happening across Sir Monty today." : `${dateStr} · Sir Monty Enterprise ERP`;

  const currentLocName = scoped
    ? locations.find((l) => l.id === user.location_id)?.name
    : (selLoc === "all" ? "All Locations" : (locations.find((l) => l.id === selLoc)?.name || "Location"));

  return (
    <DashboardHeaderCtx.Provider value={{ locationId: effectiveLoc, notifCount }}>
      <div className="flex h-screen overflow-hidden" style={{ background: C.canvas }}>
        {/* SIDEBAR */}
        <aside data-testid="app-sidebar"
          className={`${collapsed ? "w-[76px]" : "w-64"} ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static z-40 h-full flex flex-col transition-all duration-300`}
          style={{ background: C.panel, borderRight: `1px solid ${C.border}` }}>
          <div className="h-[76px] flex items-center gap-3 px-4 border-b" style={{ borderColor: C.border }}>
            <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: C.gold }}>
              <Crown size={22} color="#3B0D0D" strokeWidth={2.2} />
            </div>
            {!collapsed && (
              <div className="leading-tight min-w-0">
                <div className="font-brand font-bold text-2xl tracking-tight truncate" style={{ color: C.burgundy }}>Sir Monty</div>
                <div className="text-[10px] font-bold tracking-[0.18em] truncate" style={{ color: C.muted }}>ENTERPRISE ERP</div>
              </div>
            )}
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
            {visible.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.key} to={item.path} end={item.path === "/"} onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${item.key}`}
                  className={({ isActive }) => `flex items-center gap-3 px-3 h-10 rounded-lg text-sm font-semibold transition-colors ${isActive ? "" : "hover:bg-[#FAF6EC]"}`}
                  style={({ isActive }) => ({
                    background: isActive ? C.burgundySoft : "transparent",
                    color: isActive ? C.burgundy : C.text,
                  })}>
                  <Icon size={18} className="shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              );
            })}
          </nav>

          <button onClick={() => setCollapsed((c) => !c)} data-testid="sidebar-collapse-btn"
            className="hidden lg:flex items-center gap-2 px-4 h-12 text-sm font-medium border-t"
            style={{ borderColor: C.border, color: C.muted }}>
            <ChevronLeft className={`transition-transform ${collapsed ? "rotate-180" : ""}`} size={18} />
            {!collapsed && <span>Collapse</span>}
          </button>

          {/* Decorative tagline */}
          {!collapsed && (
            <div className="px-4 py-4 border-t" style={{ borderColor: C.border }}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg,#F4B400 0%,#E53935 100%)" }}>
                  <Crown size={18} color="#FFFFFF" strokeWidth={2.2} />
                </div>
                <div className="font-brand italic leading-tight" style={{ color: C.burgundy }}>
                  <div className="text-[13px] font-semibold">Great Food,</div>
                  <div className="text-[13px] font-semibold">Great Moments.</div>
                </div>
              </div>
            </div>
          )}
        </aside>

        {mobileOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* TOP BAR */}
          <header className="shrink-0 h-[76px] flex items-center gap-4 px-4 lg:px-6 border-b bg-white" style={{ borderColor: C.border }}>
            <button className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center hover:bg-[#FAF6EC]" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" style={{ color: C.text }}>
              <Menu size={20} />
            </button>
            <button className="hidden lg:flex w-10 h-10 rounded-lg items-center justify-center hover:bg-[#FAF6EC]" onClick={() => setCollapsed((c) => !c)} data-testid="desktop-menu-btn" style={{ color: C.text }}>
              <Menu size={20} />
            </button>

            {/* Greeting / Title */}
            <div className="min-w-0 shrink pr-4 hidden md:block">
              <div className="font-brand font-bold text-xl leading-tight truncate" style={{ color: C.text }} data-testid="topbar-title">{pageTitle}</div>
              <div className="text-[12px] truncate" style={{ color: C.softMuted }}>{pageSubtitle}</div>
            </div>

            <div className="flex-1" />

            {/* Location */}
            {!scoped && (
              <div className="hidden lg:flex items-center gap-2 h-11 px-3 rounded-lg border" style={{ borderColor: C.border, background: "#FDFBF5" }}>
                <MapPin size={15} style={{ color: C.gold }} />
                <div className="leading-tight">
                  <div className="text-[9px] font-bold tracking-[0.18em]" style={{ color: C.softMuted }}>LOCATION</div>
                  <Select value={selLoc} onValueChange={setSelLoc}>
                    <SelectTrigger className="h-5 border-0 shadow-none px-0 py-0 font-semibold text-sm bg-transparent focus:ring-0" data-testid="topbar-location">
                      <SelectValue placeholder="Location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {scoped && (
              <div className="hidden lg:flex items-center gap-2 h-11 px-3 rounded-lg border" style={{ borderColor: C.border, background: "#FDFBF5" }} data-testid="topbar-location">
                <MapPin size={15} style={{ color: C.gold }} />
                <div className="leading-tight">
                  <div className="text-[9px] font-bold tracking-[0.18em]" style={{ color: C.softMuted }}>LOCATION</div>
                  <div className="font-semibold text-sm" style={{ color: C.text }}>{currentLocName}</div>
                </div>
              </div>
            )}

            {/* Date */}
            {isDashboard && (
              <div className="hidden xl:flex items-center gap-2 h-11 px-3 rounded-lg border" style={{ borderColor: C.border, background: "#FDFBF5" }} data-testid="topbar-date">
                <Calendar size={15} style={{ color: C.gold }} />
                <div className="leading-tight">
                  <div className="text-[9px] font-bold tracking-[0.18em]" style={{ color: C.softMuted }}>DATE</div>
                  <div className="font-semibold text-sm" style={{ color: C.text }}>Today, {shortDate}</div>
                </div>
              </div>
            )}

            {/* Refresh Intelligence (dashboard only) */}
            {isDashboard && (
              <button
                data-testid="refresh-intelligence-btn"
                onClick={() => window.dispatchEvent(new CustomEvent("sirmonty:refresh-intel"))}
                className="hidden lg:flex items-center gap-2 h-11 px-4 rounded-lg border font-semibold text-sm hover:shadow-sm transition-shadow"
                style={{ borderColor: C.border, background: "#FFFFFF", color: C.text }}
              >
                <span className="w-2 h-2 rounded-sm rotate-45" style={{ background: C.gold }} />
                Refresh Intelligence
              </button>
            )}

            {/* Bell */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative w-10 h-10 rounded-full flex items-center justify-center border" style={{ borderColor: C.border, background: "#FDFBF5", color: C.text }} data-testid="topbar-bell">
                  <Bell size={17} />
                  {notifCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 text-white" style={{ background: C.burgundy }}>
                      {notifCount > 9 ? "9+" : notifCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[340px]" data-testid="notif-menu">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Notifications</span>
                  {notifs.length > 0 && (
                    <button onClick={markAllNotifs} data-testid="notif-mark-all" className="text-[11px] font-semibold" style={{ color: C.burgundy }}>Mark all read</button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.length === 0 && (
                  <div className="px-3 py-4 text-sm" style={{ color: C.softMuted }} data-testid="notif-empty">You're all caught up.</div>
                )}
                {notifs.slice(0, 8).map((n) => (
                  <DropdownMenuItem key={n.id} onClick={() => openNotif(n)} data-testid={`notif-item-${n.id}`} className="items-start gap-2 py-2.5">
                    <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: n._kind === "kds" ? C.burgundy : C.gold }} />
                    <span className="min-w-0">
                      <span className="block text-[10px] font-bold tracking-[0.14em]" style={{ color: n._kind === "kds" ? C.burgundy : C.softMuted }}>{n._kind === "kds" ? "KITCHEN" : "APPROVAL"}</span>
                      <span className="block text-[12.5px] font-medium whitespace-normal" style={{ color: C.text }}>{n.message}</span>
                      <span className="block text-[10.5px] mt-0.5" style={{ color: C.softMuted }}>{(n.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Activity log — last 30 acked/dismissed notifications, client-side */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="relative w-10 h-10 rounded-full flex items-center justify-center border hidden sm:flex"
                  style={{ borderColor: C.border, background: "#FDFBF5", color: C.text }}
                  data-testid="topbar-activity"
                  aria-label="Notification activity log"
                  title="Activity log"
                >
                  <History size={17} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[340px]" data-testid="activity-menu">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Activity log</span>
                  {activity.length > 0 && (
                    <button onClick={clearActivity} data-testid="activity-clear" className="text-[11px] font-semibold" style={{ color: C.burgundy }}>Clear</button>
                  )}
                </DropdownMenuLabel>
                <div className="px-3 pb-2 text-[10.5px]" style={{ color: C.softMuted }}>
                  Last {Math.min(activity.length, 30)} notifications you've acted on
                </div>
                <DropdownMenuSeparator />
                {activity.length === 0 && (
                  <div className="px-3 py-4 text-sm" style={{ color: C.softMuted }} data-testid="activity-empty">Nothing here yet — items you dismiss will show up.</div>
                )}
                {activity.slice(0, 12).map((a) => (
                  <DropdownMenuItem key={`${a.id}-${a.acted_at}`} onClick={() => reopenActivity(a)} data-testid={`activity-item-${a.id}`} className="items-start gap-2 py-2.5">
                    <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: a._kind === "kds" ? C.burgundy : C.gold }} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="text-[10px] font-bold tracking-[0.14em]" style={{ color: a._kind === "kds" ? C.burgundy : C.softMuted }}>{a._kind === "kds" ? "KITCHEN" : "APPROVAL"}</span>
                        <span className="text-[9.5px] font-semibold uppercase tracking-wider" style={{ color: C.softMuted }}>· {a.action}</span>
                      </span>
                      <span className="block text-[12.5px] font-medium whitespace-normal" style={{ color: C.text }}>{a.message}</span>
                      <span className="flex items-center justify-between mt-0.5">
                        <span className="text-[10.5px]" style={{ color: C.softMuted }}>{(a.acted_at || "").slice(0, 16).replace("T", " ")}</span>
                        <span className="text-[10.5px] font-semibold" style={{ color: C.burgundy }}>Reopen →</span>
                      </span>
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5" data-testid="user-menu-btn">
                  <div className="w-10 h-10 rounded-full text-white flex items-center justify-center text-sm font-bold" style={{ background: C.burgundy }}>{initials}</div>
                  <div className="hidden sm:block text-left leading-tight">
                    <div className="text-sm font-semibold" style={{ color: C.text }}>{user?.name}</div>
                    <div className="text-[11px]" style={{ color: C.softMuted }}>{user?.role?.name || "User"}</div>
                  </div>
                  <ChevronDown size={14} style={{ color: C.softMuted }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs font-normal" style={{ color: C.muted }}>{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-profile"><User size={15} className="mr-2" /> Profile & Settings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="text-[#A52222]"><LogOut size={15} className="mr-2" /> Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          <main className="flex-1 overflow-y-auto" data-testid="app-main">
            <div className="p-4 lg:p-6 max-w-[1600px] mx-auto w-full">{children}</div>
            <AssistantFAB />
            <footer className="px-4 lg:px-8 pb-4 pt-1 flex flex-wrap items-center justify-between gap-2 text-[11px]" style={{ color: C.softMuted }}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>© {now.getFullYear()} Sir Monty Enterprise ERP. All rights reserved.</span>
                <span className="opacity-60">·</span>
                <CraftedBy />
              </div>
              <div className="flex items-center gap-4">
                <span>v1.0.0</span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: "#2E7D32" }} /> All systems operational
                </span>
              </div>
            </footer>
          </main>
        </div>
      </div>
    </DashboardHeaderCtx.Provider>
  );
}
